<script setup>
    /*
========================================================================
ProductDetailsPanel.vue

Reusable component for displaying product details and size/variant/lumen
selection. Used by both the Product step modal and the Report step
"Add Product" modal.

This component manages its own local state and only emits events when
the user confirms their selection. This allows parent components to
handle store updates appropriately for their context.
========================================================================
*/
    import { ref, computed, watch, nextTick, onMounted } from 'vue';
    import { useMainStore } from '@/stores/store.js';
    import { Utils } from '@/utils.js';
    import { PhTable, PhArrowSquareOut } from '@phosphor-icons/vue';
    import InputWrap from './InputWrap.vue';
    import LumenOutputSelector from './LumenOutputSelector.vue';
    import SizeGuide from './SizeGuide.vue';

    const store = useMainStore();

    /*
	------------------------------------------------------------------------
	Props
	------------------------------------------------------------------------
	*/
    const props = defineProps({
        // Product catalog data (from store.products)
        productData: {
            type: Object,
            required: true,
        },
        // Product ID for loading extended data from store.productsExtended
        productId: {
            type: String,
            required: true,
        },
        // Show Back/Confirm buttons in sticky footer
        showFooterActions: {
            type: Boolean,
            default: false,
        },
        // Label for the confirm button
        confirmButtonLabel: {
            type: String,
            default: 'Confirm',
        },
        // Show the Back button in footer (only applies if showFooterActions is true)
        showBackButton: {
            type: Boolean,
            default: false,
        },
        // Initial shape value (for pre-populating when editing)
        initialShape: {
            type: String,
            default: '',
        },
        // Initial size value (for pre-populating when editing)
        initialSize: {
            type: String,
            default: '',
        },
        // Initial variant value (for pre-populating when editing)
        initialVariant: {
            type: String,
            default: '',
        },
        // Initial lumen output value (for pre-populating when editing)
        initialLumenOutput: {
            type: String,
            default: '',
        },
    });

    /*
	------------------------------------------------------------------------
	Emits
	------------------------------------------------------------------------
	*/
    const emit = defineEmits([
        // User confirmed their selection - payload contains complete product data
        'confirm',
        // User clicked Back button
        'back',
        // User cancelled (escape key or other cancel action)
        'cancel',
    ]);

    /*
	------------------------------------------------------------------------
	Local State
	All selection state is managed locally until user confirms.
	This prevents polluting the store with incomplete selections.
	------------------------------------------------------------------------
	*/
    const selectedShape = ref(props.initialShape);
    const selectedSize = ref(props.initialSize);
    const selectedVariant = ref(props.initialVariant);
    const selectedLumenOutput = ref(props.initialLumenOutput);
    const showSizeError = ref(false);

    /*
	------------------------------------------------------------------------
	Computed Properties
	------------------------------------------------------------------------
	*/

    // Check if product has shape variants (e.g., Tessell)
    const hasShapes = computed(() => {
        return props.productData?.hasShapes === true;
    });

    // Get available shapes for product
    const availableShapes = computed(() => {
        if (!hasShapes.value || !props.productData?.shapes) return [];
        return Object.entries(props.productData.shapes).map(([key, value]) => ({
            key,
            label: value.label || key,
        }));
    });

    // Get available sizes from product data
    const availableSizes = computed(() => {
        if (!props.productData?.sizes?.items) return [];
        return Object.entries(props.productData.sizes.items).map(([key, value]) => ({
            key,
            label: value.l,
        }));
    });

    // Check if product has variants (e.g., height variants for linear products)
    const hasVariants = computed(() => {
        if (!props.productData?.sizes?.variants?.all) return false;
        return Object.keys(props.productData.sizes.variants.all).length > 0;
    });

    // Get available variants for the selected size (and shape if applicable)
    const availableVariants = computed(() => {
        if (!hasVariants.value || !selectedSize.value) return [];
        const allVariants = props.productData.sizes.variants.all;
        return Object.entries(allVariants)
            .filter(([key, value]) => {
                if (value.size !== selectedSize.value) return false;
                // If product has shapes, also filter by selected shape
                if (hasShapes.value && value.shape !== selectedShape.value) return false;
                return true;
            })
            .map(([key, value]) => ({
                key,
                label: value.l,
            }));
    });

    // Get extended data (lumen specs) for the current size/variant selection
    const currentExtendedData = computed(() => {
        if (!props.productId || !selectedSize.value) return [];

        const extendedData = store.productsExtended?.[props.productId];
        if (!extendedData || !Array.isArray(extendedData)) return [];

        if (hasVariants.value && selectedVariant.value) {
            // Get variant info to match by label
            const variantInfo = props.productData?.sizes?.variants?.all?.[selectedVariant.value];
            if (!variantInfo) return [];
            return extendedData.filter((item) => {
                if (item.size !== selectedSize.value || item.variant !== variantInfo.l) return false;
                if (hasShapes.value && item.shape !== selectedShape.value) return false;
                return true;
            });
        } else {
            // No variant - filter by size only
            return extendedData.filter((item) => {
                if (item.size !== selectedSize.value || item.variant !== '') return false;
                if (hasShapes.value && item.shape !== selectedShape.value) return false;
                return true;
            });
        }
    });

    /*
	------------------------------------------------------------------------
	Auto-select Single Options
	When there is only one option in a select list, automatically select it.
	This improves UX by reducing clicks for products with single options.
	------------------------------------------------------------------------
	*/

    // Watch for single size option and auto-select
    watch(
        () => [availableSizes.value, selectedSize.value],
        ([sizes, currentSize]) => {
            // Only auto-select when there's exactly one option and nothing selected
            if (sizes.length === 1 && !currentSize) {
                $log.log(`[ProductDetailsPanel:watch] Auto-selecting single size option: ${sizes[0].key}`);
                nextTick(() => {
                    handleSizeChange(sizes[0].key);
                });
            }
        },
        { immediate: true },
    );

    // Watch for single shape option and auto-select
    watch(
        () => [availableShapes.value, selectedShape.value],
        ([shapes, currentShape]) => {
            if (shapes.length === 1 && !currentShape) {
                $log.log(`[ProductDetailsPanel:watch] Auto-selecting single shape option: ${shapes[0].key}`);
                nextTick(() => {
                    handleShapeChange(shapes[0].key);
                });
            }
        },
        { immediate: true },
    );

    // Watch for single variant option and auto-select
    watch(
        () => [availableVariants.value, selectedVariant.value, selectedSize.value],
        ([variants, currentVariant, currentSize]) => {
            // Only auto-select when size is selected and there's exactly one variant
            if (currentSize && variants.length === 1 && !currentVariant) {
                $log.log(`[ProductDetailsPanel:watch] Auto-selecting single variant option: ${variants[0].key}`);
                nextTick(() => {
                    handleVariantChange(variants[0].key);
                });
            }
        },
        { immediate: true },
    );

    /*
	------------------------------------------------------------------------
	Event Handlers
	------------------------------------------------------------------------
	*/

    // Handle shape selection change - clears all dependent selections
    const handleShapeChange = (newShape) => {
        selectedShape.value = newShape;
        selectedSize.value = '';
        selectedVariant.value = '';
        selectedLumenOutput.value = '';
        showSizeError.value = false;
    };

    // Handle size selection change
    const handleSizeChange = (newSize) => {
        selectedSize.value = newSize;
        selectedVariant.value = ''; // Clear variant when size changes
        selectedLumenOutput.value = ''; // Clear lumen output when size changes
        showSizeError.value = false;
    };

    // Handle variant selection change
    const handleVariantChange = (newVariant) => {
        selectedVariant.value = newVariant;
        selectedLumenOutput.value = ''; // Clear lumen output when variant changes
        showSizeError.value = false;
    };

    // Handle lumen output selection change (from LumenOutputSelector component)
    const handleLumenOutputChange = (newValue) => {
        selectedLumenOutput.value = newValue;
        showSizeError.value = false;
    };

    /*
	------------------------------------------------------------------------
	Confirm Handler
	Validates selection and emits confirm event with complete product payload.
	Parent component is responsible for adding/updating store.
	------------------------------------------------------------------------
	*/
    const handleConfirm = () => {
        // Validate shape is selected (for products with shapes)
        if (hasShapes.value && !selectedShape.value) {
            $log.warn('[ProductDetailsPanel:handleConfirm] No shape selected');
            showSizeError.value = true;
            return;
        }

        // Validate size is selected
        if (!selectedSize.value) {
            $log.warn('[ProductDetailsPanel:handleConfirm] No size selected');
            showSizeError.value = true;
            return;
        }

        // Validate variant if product has variants for the selected size
        if (hasVariants.value && availableVariants.value.length > 0 && !selectedVariant.value) {
            $log.warn('[ProductDetailsPanel:handleConfirm] No variant selected');
            showSizeError.value = true;
            return;
        }

        // Validate lumen output if product has lumen outputs
        if (currentExtendedData.value.length > 0 && !selectedLumenOutput.value) {
            $log.warn('[ProductDetailsPanel:handleConfirm] No lumen output selected');
            showSizeError.value = true;
            return;
        }

        // Get metric sabins based on selection (from variant or size data)
        let metricSabins = '';
        if (hasVariants.value && selectedVariant.value) {
            const variantData = props.productData.sizes.variants.all[selectedVariant.value];
            metricSabins = variantData?.metric_sabins || '';
        } else {
            const sizeData = props.productData.sizes.items[selectedSize.value];
            metricSabins = sizeData?.metric_sabins || '';
        }

        // Get filtered extended data for the selection
        let extendedData = [];
        if (store.productsExtended && store.productsExtended[props.productId]) {
            if (hasVariants.value && selectedVariant.value) {
                const variantInfo = props.productData.sizes.variants.all[selectedVariant.value];
                extendedData = store.productsExtended[props.productId].filter((item) => {
                    if (item.size !== selectedSize.value || item.variant !== variantInfo.l) return false;
                    if (hasShapes.value && item.shape !== selectedShape.value) return false;
                    return true;
                });
            } else {
                extendedData = store.productsExtended[props.productId].filter((item) => {
                    if (item.size !== selectedSize.value || item.variant !== '') return false;
                    if (hasShapes.value && item.shape !== selectedShape.value) return false;
                    return true;
                });
            }
        }

        // Emit confirm event with complete product payload
        emit('confirm', {
            productId: props.productId,
            shape: selectedShape.value,
            size: selectedSize.value,
            size_variant: selectedVariant.value,
            metric_sabins: metricSabins,
            data: props.productData,
            dataExtended: extendedData,
            selectedLumenOutput: selectedLumenOutput.value,
        });
    };

    // Handle back button click
    const handleBack = () => {
        emit('back');
    };

    // Handle cancel/escape
    const handleCancel = () => {
        emit('cancel');
    };

    /*
	------------------------------------------------------------------------
	Expose methods for parent components if needed
	------------------------------------------------------------------------
	*/
    defineExpose({
        handleConfirm,
        handleCancel,
    });
</script>

<template>
    <!--
    ========================================================================
    Product Details Panel Layout
    Two-column layout: product image on left, details/form on right
    ========================================================================
    -->
    <div class="flex h-full flex-col sm:flex-row">
        <!--
        --------------------------------------------------------------------
        Left Section: Product Image (sticky on desktop)
        --------------------------------------------------------------------
        -->
        <div class="flex-0 min-h-[150px] sm:sticky sm:right-0 sm:top-0 sm:h-screen sm:w-[35%] md:w-[35%]">
            <img class="absolute left-3 top-0 mx-auto max-w-[150px] sm:max-w-[180px] md:max-w-[300px]" :src="Utils.resolveHeroImage(productData.images.hero, selectedShape)" :alt="`Product image of: ${productData.name}.`" />
        </div>

        <!--
        --------------------------------------------------------------------
        Right Section: Product Details and Selection Form
        --------------------------------------------------------------------
        -->
        <div class="lux-modal-content relative flex h-screen flex-1 flex-col overflow-y-auto px-8 sm:w-[65%] md:w-[65%]">
            <!-- Product Name -->
            <h2 class="flex-0 lux-page-heading pb-0 pr-8 pt-8 font-normal">
                {{ productData.name }}
            </h2>

            <!--
            ----------------------------------------------------------------
            Size/Variant/Lumen Selection Form
            ----------------------------------------------------------------
            -->
            <div class="flex-0 mb-4 flex flex-col gap-2 bg-white">
                <!-- Shape Select (only for products with shapes) -->
                <InputWrap v-if="hasShapes" label="Product Shape" :required="true" :wrapClass="showSizeError && !selectedShape ? 'error' : ''">
                    <select class="lx-field-input-wrap lux-focus-ring" :class="{ error: showSizeError && !selectedShape }" :value="selectedShape" @change="handleShapeChange($event.target.value)">
                        <option value="" selected>Select...</option>
                        <option v-for="shape in availableShapes" :key="shape.key" :value="shape.key">
                            {{ shape.label }}
                        </option>
                    </select>
                </InputWrap>

                <!-- Size Select -->
                <InputWrap label="Product Size" :required="true" :wrapClass="showSizeError && !selectedSize ? 'error' : ''">
                    <select class="lx-field-input-wrap lux-focus-ring" :class="{ error: showSizeError && !selectedSize }" :value="selectedSize" @change="handleSizeChange($event.target.value)">
                        <option value="" selected>Select...</option>
                        <option v-for="size in availableSizes" :key="size.key" :value="size.key">
                            {{ size.label }}
                        </option>
                    </select>
                </InputWrap>

                <!-- Variant Select (conditional - only shown if product has variants for selected size) -->
                <div v-if="hasVariants && availableVariants.length > 0">
                    <InputWrap label="Height Variant" :required="true" :wrapClass="showSizeError && selectedSize && !selectedVariant ? 'error' : ''">
                        <select class="lx-field-input-wrap lux-focus-ring" :class="{ error: showSizeError && selectedSize && !selectedVariant }" :value="selectedVariant" @change="handleVariantChange($event.target.value)">
                            <option value="" selected>Select...</option>
                            <option v-for="variant in availableVariants" :key="variant.key" :value="variant.key">
                                {{ variant.label }}
                            </option>
                        </select>
                    </InputWrap>
                </div>

                <!-- Lumen Output Selector (uses shared component) -->
                <LumenOutputSelector :dataExtended="currentExtendedData" :selectedValue="selectedLumenOutput" :showErrors="showSizeError" context="Modal" @update:selectedValue="handleLumenOutputChange" />

                <!-- Validation Error Message -->
                <p v-if="showSizeError && ((hasShapes && !selectedShape) || !selectedSize || (hasVariants && availableVariants.length > 0 && !selectedVariant) || (currentExtendedData.length > 0 && !selectedLumenOutput))" class="text-sm text-red-600">
                    Please complete all required fields to continue.
                </p>
            </div>

            <!--
            ----------------------------------------------------------------
            Product Description and Spec Sheet Link
            ----------------------------------------------------------------
            -->
            <div class="flex-0 mb-5 sm:pr-5">
                <p class="lux-copy-normal first-of-type:mt-0" v-html="productData.desc"></p>
                <p class="mt-5 flex items-center gap-1">
                    <PhTable :size="16" weight="regular" aria-hidden="true" />
                    <a :href="productData.spec_sheet_permalink" target="_blank" class="text-sm underline outline-none hover:underline focus:ring-2 focus:ring-accent focus:ring-offset-1" title="Opens in a new tab/window." rel="noopener">
                        View the spec sheet.
                    </a>
                    <PhArrowSquareOut :size="11" weight="regular" aria-hidden="true" class="relative -mt-2" />
                    <span class="sr-only">(link opens in a new window)</span>
                </p>
            </div>

            <!--
            ----------------------------------------------------------------
            Size Guide Images (collapsible)
            ----------------------------------------------------------------
            -->
            <SizeGuide
                :images="productData.images.dimensions"
                :productName="productData.name"
                :wrapperClass="showFooterActions ? 'pb-32' : 'pb-20'"
            />

            <!--
            ----------------------------------------------------------------
            Footer Actions (optional - controlled by showFooterActions prop)
            Used by Report step modal for Back/Add Product buttons.
            Product step modal uses its own close button instead.
            ----------------------------------------------------------------
            -->
            <div v-if="showFooterActions" class="sticky bottom-0 flex justify-between gap-4 border-t border-gray-200 bg-white py-6">
                <button v-if="showBackButton" type="button" class="lux-btn lux-btn-outline" @click="handleBack">Back</button>
                <div v-else></div>
                <button type="button" class="lux-btn lux-btn-black" @click="handleConfirm">
                    {{ confirmButtonLabel }}
                </button>
            </div>
        </div>
    </div>
</template>

<style scoped>
    /* Component-specific styles can be added here if needed */
</style>
